import { useEffect, useLayoutEffect, useRef } from 'react';

export type CanvasSize = { width: number; height: number };

/** Draws one frame onto an already-cleared canvas. Return false to end the animation. `dt` is in milliseconds. */
export type CanvasStep = (ctx: CanvasRenderingContext2D, dt: number, size: CanvasSize) => boolean | void;

export const prefersReducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Runs a requestAnimationFrame loop on a canvas sized to its own CSS box. Frames are drawn straight to the canvas
 * (no React state per frame - see GhostAnimation for why). `createStep` is called each time the animation starts, so
 * changing `runKey` replays it from scratch. Nothing runs when the user prefers reduced motion.
 */
export const useCanvasAnimation = ({
  createStep,
  enabled = true,
  runKey = 0,
}: {
  createStep: (size: CanvasSize) => CanvasStep;
  enabled?: boolean;
  runKey?: number;
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const createStepRef = useRef(createStep);
  useLayoutEffect(() => {
    createStepRef.current = createStep;
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !enabled || prefersReducedMotion()) return undefined;

    const fit = (): CanvasSize => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { width: rect.width, height: rect.height };
    };

    let size = fit();
    const step = createStepRef.current(size);
    const onResize = () => {
      size = fit();
    };
    window.addEventListener('resize', onResize);

    let frame = 0;
    let last = performance.now();
    const animate = (now: number) => {
      // clamp so a background tab doesn't jump everything forward when it wakes up
      const dt = Math.min(now - last, 50);
      last = now;
      // clear the whole current canvas here rather than in each step: steps size things off the size they started
      // with, and clearing only that area leaves smears behind after the window grows
      ctx.clearRect(0, 0, size.width, size.height);
      if (step(ctx, dt, size) === false) {
        ctx.clearRect(0, 0, size.width, size.height);
        return;
      }
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      ctx.clearRect(0, 0, size.width, size.height);
    };
  }, [enabled, runKey]);

  return canvasRef;
};

export const random = (min: number, max: number) => min + Math.random() * (max - min);
